import React, { useMemo, useState, useEffect, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import PublicLayout from '../components/layouts/PublicLayout';
import api from '../api/api';

const ASSETS = {
  background: '/assets/contctbg.png',
  emojis: {
    circle: '/assets/guest/crl.svg',
    square: '/assets/guest/sq.svg',
    triangle: '/assets/guest/tri.svg',
    hexagon: '/assets/guest/pent.svg',
  },
};

const initialFormState = {
  name: '',
  email: '',
  phone: '',
  message: '',
};

const Contact = () => {
  const location = useLocation();
  const [formData, setFormData] = useState(initialFormState);
  const [status, setStatus] = useState('idle');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Scroll to top when component mounts or location changes
  useLayoutEffect(() => {
    // Immediate scroll to top
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location.pathname]);

  // Also handle smooth scroll after a brief delay to ensure page is rendered
  useEffect(() => {
    const timer = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const isSubmitDisabled = useMemo(() => {
    if (isSubmitting) return true;
    return !formData.name.trim() || !formData.email.trim() || !formData.message.trim();
  }, [formData, isSubmitting]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setStatus('submitting');
    setErrors({}); // Clear previous errors

    try {
      // Prepare payload - only include phone if it has a value
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        message: formData.message.trim(),
      };

      // Add phone only if provided
      if (formData.phone && formData.phone.trim()) {
        payload.phone = formData.phone.trim();
      }

      await api.post('/queries', payload);
      
      // If we reach here, the request was successful
      // API interceptor handles the response transformation
      setStatus('success');
      setFormData(initialFormState);
      setErrors({});
    } catch (error) {
      console.error('Contact form submission failed', error);
      
      // Handle validation errors (422)
      if (error.response?.status === 422) {
        const validationErrors = error.response.data?.result?.data?.errors || {};
        
        // Convert errors object to field-specific errors
        const fieldErrors = {};
        Object.keys(validationErrors).forEach((field) => {
          // Get the first error message for each field
          const errorMessage = Array.isArray(validationErrors[field]) 
            ? validationErrors[field][0] 
            : validationErrors[field];
          
          // Format specific field errors
          if (field === 'message') {
            // Check if error is about minimum length (10 characters)
            if (errorMessage.toLowerCase().includes('10') || 
                errorMessage.toLowerCase().includes('at least') ||
                errorMessage.toLowerCase().includes('minimum')) {
              fieldErrors[field] = 'Message must be at least 10 characters long';
            } else {
              fieldErrors[field] = errorMessage;
            }
          } else if (field === 'email') {
            // Format email validation errors
            const lowerMessage = errorMessage.toLowerCase();
            if (lowerMessage.includes('valid') || lowerMessage.includes('format')) {
              fieldErrors[field] = 'Please provide a valid email address';
            } else if (lowerMessage.includes('required')) {
              fieldErrors[field] = 'Email address is required';
            } else {
              fieldErrors[field] = errorMessage;
            }
          } else {
            fieldErrors[field] = errorMessage;
          }
        });
        
        setErrors(fieldErrors);
        setStatus('error');
      } else {
        // Handle other errors
        setStatus('error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PublicLayout showSupportCTA={false}>
      <div className="bg-bodyMain min-h-screen">
        {/* Hero */}
        <section className="relative overflow-hidden bg-white flex items-center justify-center min-h-[260px] sm:min-h-[420px] lg:min-h-[480px]">
          <div className="absolute inset-0">
            <img
              src={ASSETS.background}
              alt="Contact background"
              className="w-full h-full object-cover"
              style={{ opacity: 0.15 }}
            />
          </div>
          <div className="absolute inset-0 pointer-events-none">
            <img
              src={ASSETS.emojis.circle}
              alt="smiley circle"
              className="absolute w-20 h-20 left-2 top-4 sm:w-36 sm:h-36 sm:left-40 sm:top-6"
              style={{
                filter: 'drop-shadow(0 12px 28px rgba(139, 92, 246, 0.35))',
                transform: 'rotate(-45deg)',
              }}
            />
            <img
              src={ASSETS.emojis.square}
              alt="smiley square"
              className="absolute w-14 h-14 left-16 bottom-2 sm:w-20 sm:h-20 sm:left-96 sm:bottom-8 animate-float"
              style={{ filter: 'drop-shadow(0 10px 24px rgba(254, 0, 122, 0.35))' }}
            />
            <img
              src={ASSETS.emojis.triangle}
              alt="smiley triangle"
              className="absolute w-14 h-14 right-4 top-8 sm:w-24 sm:h-24 sm:right-48 sm:top-12"
              style={{ filter: 'drop-shadow(0 10px 24px rgba(255, 182, 0, 0.35))' }}
            />
            <img
              src={ASSETS.emojis.hexagon}
              alt="smiley hexagon"
              className="absolute w-20 h-20 right-16 bottom-6 sm:w-32 sm:h-32 sm:right-96 sm:bottom-10 animate-float-slow"
              style={{ filter: 'drop-shadow(0 12px 28px rgba(16, 185, 129, 0.35))' }}
            />
          </div>

          <div className="relative max-w-6xl mx-auto px-6 sm:px-12 py-20 sm:py-24">
            <div className="text-center">
              <h1 className="font-extrabold tracking-tight text-[48px] sm:text-[60px] lg:text-[78.6px] leading-[1.05]">
                <span className="text-[#a463ff]">CONTACT </span>
                <span className="text-[#000000]">US</span>
              </h1>
            </div>
          </div>
        </section>

        <style jsx>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-18px); }
          }

          @keyframes float-slow {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(22px); }
          }

          .animate-float {
            animation: float 7s ease-in-out infinite;
          }

          .animate-float-slow {
            animation: float-slow 9s ease-in-out infinite;
          }
        `}</style>

        {/* Contact Content */}
        <section className="relative py-12 sm:py-24">
          <div className="w-full px-6 sm:px-12 lg:px-16 xl:px-24">
            <header className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 w-full">
                <h2 className="font-extrabold leading-tight text-[40px] sm:text-[48px] lg:text-[53px] text-[#000000] lg:max-w-3xl">
                  <span className="text-[#ffb600]">Get in touch</span> with us.
                  <br />
                  We&apos;re here to <span className="text-[#10B981]">assist you.</span>
                </h2>
              <div className="flex-1 lg:flex lg:justify-end">
                <p className="text-[20.75px] text-[#5E5C61] lg:max-w-sm lg:text-left">
                    Popout is a smart platform for families to discover, book, and manage enriching activities for children.
                  </p>
                </div>
            </header>

            <form onSubmit={handleSubmit} className="mt-12 space-y-10">
                <div className="grid md:grid-cols-3 gap-8">
                  <div className="md:col-span-1">
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your Name"
                      className={`w-full border-0 border-b rounded-none focus:border-[#fe007a] transition-colors bg-transparent pb-3 text-[#5E5C61] placeholder:text-[#A0A0A0] focus:placeholder:text-[#fe007a] focus:outline-none focus:ring-0 ${
                        errors.name ? 'border-red-500' : 'border-[#d9d9d9]'
                      }`}
                      required
                    />
                    {errors.name && (
                      <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                    )}
                  </div>
                  <div className="md:col-span-1">
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Email Address"
                      className={`w-full border-0 border-b rounded-none focus:border-[#fe007a] transition-colors bg-transparent pb-3 text-[#5E5C61] placeholder:text-[#A0A0A0] focus:placeholder:text-[#fe007a] focus:outline-none focus:ring-0 ${
                        errors.email ? 'border-red-500' : 'border-[#d9d9d9]'
                      }`}
                      required
                    />
                    {errors.email && (
                      <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                    )}
                  </div>
                  <div className="md:col-span-1">
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Phone Number (optional)"
                      className={`w-full border-0 border-b rounded-none focus:border-[#fe007a] transition-colors bg-transparent pb-3 text-[#5E5C61] placeholder:text-[#A0A0A0] focus:placeholder:text-[#fe007a] focus:outline-none focus:ring-0 ${
                        errors.phone ? 'border-red-500' : 'border-[#d9d9d9]'
                      }`}
                    />
                    {errors.phone && (
                      <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
                    )}
                  </div>
                </div>

                <div>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Message"
                    rows={4}
                    className={`w-full border-0 border-b rounded-none focus:border-[#fe007a] transition-colors bg-transparent pb-3 text-[#5E5C61] placeholder:text-[#A0A0A0] focus:placeholder:text-[#fe007a] focus:outline-none focus:ring-0 resize-none ${
                      errors.message ? 'border-red-500' : 'border-[#d9d9d9]'
                    }`}
                    required
                  />
                  {errors.message && (
                    <p className="text-red-500 text-xs mt-1">{errors.message}</p>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <button
                    type="submit"
                    disabled={isSubmitDisabled}
                    className="inline-flex items-center justify-center rounded-full bg-[#FE007A] px-8 sm:px-10 py-3.5 text-white text-sm sm:text-base font-semibold shadow-lg transition hover:bg-[#db0069] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isSubmitting ? 'Sending...' : 'Leave us a Message'}
                  </button>

                  <div className="text-sm text-[#5E5C61]">
                    {status === 'success' && (
                      <span className="font-medium text-[#10B981]">
                        Thank you for contacting us! We will get back to you soon.
                      </span>
                    )}
                    {status === 'error' && Object.keys(errors).length === 0 && (
                      <span className="font-medium text-red-500">
                        Something went wrong. Please try again later.
                      </span>
                    )}
                  </div>
                </div>
            </form>
          </div>
        </section>
      </div>
    </PublicLayout>
  );
};

export default Contact;

